/**
 * Client Detail Page Component
 *
 * Displays detailed client information with portfolio positions in a DataGrid
 * and loan details, replacing the previous DataTable implementation.
 */
import { useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { advisorClient } from '../api/client';
import { AdvisorServiceMissingError, AdvisorServiceNotice, advisorFailure } from '../components/AdvisorServiceNotice';
import type { components } from '@aaimplatform/institutional-types/advisor-tools';
import type { ColumnDef } from '@aaimplatform/ui';
import {
  Heading,
  BodyText,
  Card,
  Button,
  MetricCard,
  DataGrid,
  DashboardGrid,
  DashboardWidget,
  BackLink,
} from '@aaimplatform/ui';


type ClientDetails = components['schemas']['ClientDetails'];

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
}

function formatDate(dateString: string): string {
  return new Date(dateString).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

const LOAN_STATUS_STYLES: Record<string, string> = {
  active: 'text-growth bg-growth/10 border-growth/20',
  pending: 'text-accent bg-accent/5 border-accent/20',
  closed: 'text-tertiary bg-recessed border-surface',
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function ClientDetailPage() {
  const { clientId } = useParams<{ clientId: string }>();
  const navigate = useNavigate();

  const { data: client, isLoading, error } = useQuery<ClientDetails>({
    queryKey: ['client', clientId],
    queryFn: async () => {
      const { data, error, response } = await advisorClient.GET('/api/clients/{clientId}', {
        params: { path: { clientId: clientId! } },
      });
      if (error || !response.ok) throw advisorFailure(response, error, 'Failed to load client details');
      return data;
    },
  });

  // Position columns for DataGrid
  const positionColumns = useMemo<ColumnDef<ClientDetails['positions'][0], unknown>[]>(() => [
    {
      id: 'symbol',
      header: 'Symbol',
      accessorKey: 'symbol',
      size: 100,
      enableSorting: true,
      cell: ({ getValue }) => (
        <span className="font-semibold font-mono text-primary">{String(getValue())}</span>
      ),
    },
    {
      id: 'description',
      header: 'Description',
      accessorKey: 'description',
      size: 220,
      enableSorting: true,
    },
    {
      id: 'quantity',
      header: 'Quantity',
      accessorKey: 'quantity',
      size: 110,
      enableSorting: true,
      meta: { align: 'right' },
      cell: ({ getValue }) => (
        <span className="font-mono tabular-nums">{Number(getValue()).toLocaleString()}</span>
      ),
    },
    {
      id: 'marketValue',
      header: 'Market Value',
      accessorKey: 'marketValue',
      size: 140,
      enableSorting: true,
      meta: { align: 'right' },
      cell: ({ getValue }) => (
        <span className="font-mono tabular-nums">{formatCurrency(Number(getValue()))}</span>
      ),
    },
    {
      id: 'collateralValue',
      header: 'Collateral Value',
      accessorKey: 'collateralValue',
      size: 150,
      enableSorting: true,
      meta: { align: 'right' },
      cell: ({ row, getValue }) =>
        row.original.isEligible ? (
          <span className="font-mono tabular-nums">{formatCurrency(Number(getValue()))}</span>
        ) : (
          <span className="text-tertiary">N/A</span>
        ),
    },
    {
      id: 'eligible',
      header: 'Status',
      accessorFn: (row) => (row.isEligible ? 'Eligible' : 'Ineligible'),
      size: 110,
      enableSorting: true,
      cell: ({ row }) =>
        row.original.isEligible ? (
          <span className="inline-block px-2 py-0.5 rounded text-xs font-semibold bg-growth/10 text-growth">
            Eligible
          </span>
        ) : (
          <span className="inline-block px-2 py-0.5 rounded text-xs font-semibold bg-recessed text-tertiary">
            Ineligible
          </span>
        ),
    },
  ], []);

  if (isLoading) {
    return (
      <Card>
        <BodyText className="text-secondary text-center py-8">Loading client details...</BodyText>
      </Card>
    );
  }

  if (error instanceof AdvisorServiceMissingError) {
    return (
      <div>
        <BackLink href="/clients" className="mb-4">
          Back to Clients
        </BackLink>
        <AdvisorServiceNotice />
      </div>
    );
  }

  if (!client) {
    return (
      <Card>
        <BodyText className="text-secondary text-center py-8">Client not found</BodyText>
      </Card>
    );
  }

  return (
    <div>
      <BackLink href="/clients" className="mb-4">
        Back to Clients
      </BackLink>

      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between mb-6">
        <div>
          <BodyText className="text-secondary mt-2">
            {client.email} | {client.phone}
          </BodyText>
        </div>
        <div className="flex flex-wrap gap-3">
          <Button variant="primary" onClick={() => navigate(`/calculator?clientId=${client.id}`)}>
            Calculate Pledging Capacity
          </Button>
          <Button variant="secondary" onClick={() => navigate(`/proposals/new?clientId=${client.id}`)}>
            Create Proposal
          </Button>
        </div>
      </div>

      {/* Summary Metrics */}
      <DashboardGrid columns={4} gap="compact" className="mb-6">
        <DashboardWidget id="market-value">
          <MetricCard
            label="Total Market Value"
            value={formatCurrency(client.summary.totalMarketValue)}
            valueColor="primary"
          />
        </DashboardWidget>
        <DashboardWidget id="collateral-value">
          <MetricCard
            label="Total Collateral Value"
            value={formatCurrency(client.summary.totalCollateralValue)}
            valueColor="primary"
          />
        </DashboardWidget>
        <DashboardWidget id="available-borrow">
          <MetricCard
            label="Available to Borrow"
            value={formatCurrency(client.summary.availableCollateral)}
            valueColor="growth"
          />
        </DashboardWidget>
        <DashboardWidget id="total-borrowed">
          <MetricCard
            label="Total Borrowed"
            value={formatCurrency(client.summary.totalBorrowed)}
            valueColor="primary"
          />
        </DashboardWidget>
      </DashboardGrid>

      {/* Portfolio Positions DataGrid */}
      <div className="mb-6">
        <Heading level={3} as={3} size="card" className="mb-4">Portfolio Positions</Heading>
        <DataGrid
          data={client.positions}
          columns={positionColumns}
          enableVirtualization={client.positions.length > 50}
          virtualizedHeight={400}
          enableGlobalFilter
          enableColumnFilters
          enableExport
          enablePagination={client.positions.length > 25}
          defaultPageSize={25}
          striped
          hoverable
          density="compact"
          getRowId={(row) => row.id ?? row.symbol}
          emptyMessage="No portfolio positions on file"
        />
      </div>

      {/* Loans */}
      <div>
        <Heading level={3} as={3} size="card" className="mb-4">Loans</Heading>
        {client.loans.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {client.loans.map((loan) => (
              <Card key={loan.id}>
                <div className="flex justify-between items-center mb-3">
                  <BodyText className="font-mono font-semibold text-primary">#{loan.loanNumber}</BodyText>
                  <span
                    className={`px-3 py-1 rounded-full text-xs font-semibold border ${
                      LOAN_STATUS_STYLES[loan.status.toLowerCase()] ?? 'text-primary bg-surface border-recessed'
                    }`}
                  >
                    {loan.status}
                  </span>
                </div>
                <div className="space-y-2">
                  <div className="flex justify-between items-center">
                    <BodyText className="text-tertiary text-sm">Principal</BodyText>
                    <BodyText className="font-mono text-primary">{formatCurrency(loan.principalAmount)}</BodyText>
                  </div>
                  <div className="flex justify-between items-center">
                    <BodyText className="text-tertiary text-sm">Outstanding</BodyText>
                    <BodyText className="font-mono font-semibold text-primary">
                      {formatCurrency(loan.outstandingBalance)}
                    </BodyText>
                  </div>
                  <div className="flex justify-between items-center">
                    <BodyText className="text-tertiary text-sm">Interest Rate</BodyText>
                    <BodyText className="font-mono text-primary">{(loan.interestRate * 100).toFixed(2)}%</BodyText>
                  </div>
                  <div className="flex justify-between items-center">
                    <BodyText className="text-tertiary text-sm">Originated</BodyText>
                    <BodyText className="text-primary text-sm">{formatDate(loan.originationDate)}</BodyText>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        ) : (
          <Card>
            <BodyText className="text-secondary text-center py-8">No active loans</BodyText>
          </Card>
        )}
      </div>
    </div>
  );
}
